import React from 'react';
import { Modal, Group, Button, Text, Box, Radio, Input, CopyButton, Divider, Switch, Stack, rem } from '@mantine/core';
import { IconLock, IconBuilding, IconSettings, IconLink, IconCopy, IconCheck, IconLinkPlus } from '@tabler/icons-react';
import QRCode from 'react-qr-code';
import EmbedCodeBlock from '@/components/Chat/EmbedCodeBlock';
import ShareParamLinkModal from '@/components/Chat/ShareParamLinkModal';
import { showNotification } from '@mantine/notifications';
import { useGPTStore } from '@/store';
import { useAdminStore } from '@/store/admin';
import { setResourcePrinciple, getResourcePrinciple } from '@mygpt/app/apis/services/admin';
import { downloadQRCode, updateRobotConfigs } from '@mygpt/app';
import LocalMemberSelector from '@/components/Access/LocalMemberSelector';
import { useState, useEffect } from 'react';
import CustomUserMultiSelect from '@/components/Access/CustomUserMultiSelect';
import { useTranslation } from 'react-i18next';
import { updateRobot } from '@mygpt/app/apis';

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  authorityCodes: string[];
  departmentIds?: string[];
  roles: { id: number; code: string; name: string }[];
}

interface ShareSettingModalProps {
  opened: boolean;
  onClose: () => void;
  botId: string;
  dataType: 'BOT' | 'DATASET';
  authorityCode: string;
  isOwner?: boolean;
}

const ShareSettingModal: React.FC<ShareSettingModalProps> = ({
  opened,
  onClose,
  botId,
  dataType,
  authorityCode,
  isOwner = true,
}) => {
  const { t } = useTranslation();
  // 状态 - 初始值设为 undefined，表示还未确定
  const [shareTo, setShareTo] = React.useState<'me' | 'org' | 'custom' | 'visitByLink' | undefined>(undefined);
  const { userInfo, robotInfo, setRobotInfo, assetScope } = useGPTStore();
  const { departmentUsersList, isDataReady, fetchAllAdminData, companyId, userOgsId } = useAdminStore();

  // Cloud版模式检测
  const isLocalMode = import.meta.env.VITE_MODE === 'local';
  const isCloudMode = !isLocalMode;
  const isCloudEnterprise = isCloudMode && !!assetScope;
  const hasOrganization = isCloudMode && !!companyId;
  const [saving, setSaving] = useState(false);
  const [localSelectorOpen, setLocalSelectorOpen] = useState(false);
  const [customSelectedUsers, setCustomSelectedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentSelectedUserIds, setCurrentSelectedUserIds] = useState<string[]>([]);
  // 分享页送信前需同意利用规约(bot configs: enable_chat_consent)
  const [requireConsent, setRequireConsent] = useState(false);
  // 带参数分享链接弹窗
  const [paramLinkModalOpened, setParamLinkModalOpened] = useState(false);

  // 弹窗打开时从 bot configs 同步开关状态
  useEffect(() => {
    if (opened) {
      setRequireConsent(!!robotInfo?.configs?.enable_chat_consent);
    }
  }, [opened, robotInfo?.configs?.enable_chat_consent]);

  // 开关即时保存(不依赖底部「保存」按钮,任意分享模式下均可配置)
  const handleConsentToggle = async (value: boolean) => {
    setRequireConsent(value);
    try {
      await updateRobotConfigs({
        aiId: botId,
        configs: { enable_chat_consent: value },
      });
    } catch (e) {
      setRequireConsent(!value);
      showNotification({ message: t('shareSetting.saveFailed'), color: 'red' });
    }
  };

  // 生成分享链接：本地部署版用 /share/ 路径，Cloud版用 web 应用的 /bot/ 路径
  const shareLink = isLocalMode
    ? `${window.location.origin}/share/${botId}`
    : `${import.meta.env.VITE_BASE_HOST}/bot/${botId}`;

  // 嵌入代码相关 host（参考 widget Embed Code 的环境判断）
  const embedBaseHost = isLocalMode ? window.location.origin : import.meta.env.VITE_BASE_HOST;

  const onCloseHandler = () => {
    setCustomSelectedUsers([]);
    setCurrentSelectedUserIds([]);
    onClose();
  };

  // 当弹窗打开时，确保组织数据已加载（本地部署版和Cloud企业模式）
  useEffect(() => {
    if (opened && !isDataReady && (isLocalMode || isCloudEnterprise)) {
      console.log('[ShareSettingModal] Data not ready, fetching admin data...');
      // 添加防重复调用逻辑
      const { isDataLoading } = useAdminStore.getState();
      if (!isDataLoading) {
        fetchAllAdminData().catch((error) => {
          console.error('[ShareSettingModal] Failed to fetch admin data:', error);
        });
      }
    }
  }, [opened, isDataReady, fetchAllAdminData]);

  // 根据当前用户email查找对应的用户id
  const getCurrentUserId = (): string => {
    if (!userInfo?.email || !departmentUsersList) {
      console.warn('[ShareSettingModal] userInfo.email or departmentUsersList not available');
      return userInfo?.user_id || ''; // 降级使用userInfo.user_id，如果也没有则返回空字符串
    }

    // 优先用OGS userId匹配（email可能带OAuth前缀导致不匹配）
    const currentUser = userOgsId
      ? departmentUsersList.find((user: User) => String(user.id) === userOgsId)
      : departmentUsersList.find((user: User) => user.email === userInfo.email);
    if (currentUser) {
      console.log('[ShareSettingModal] Found current user:', currentUser);
      return String(currentUser.id);
    } else {
      console.warn('[ShareSettingModal] Current user not found in departmentUsersList, using userInfo.user_id');
      return userInfo.user_id || '';
    }
  };

  // 获取当前权限设置并回显
  const fetchCurrentPermission = async () => {
    if (!opened) return;

    setLoading(true);
    try {
      const res = await getResourcePrinciple({
        dataId: botId,
        dataType,
        authorityCode,
      });
      const data = res.data || res;
      console.log('[ShareSettingModal] Current permission data:', data);

      if (data.userIds && data.userIds.length > 0) {
        // 优先判断userIds
        const currentUserId = getCurrentUserId();
        const onlySelf = data.userIds.length === 1 && String(data.userIds[0]) === String(currentUserId);
        if (onlySelf) {
          setShareTo('me');
          setCurrentSelectedUserIds([]);
        } else {
          setShareTo('custom');
          const userIds = data.userIds.map((userId: string) => String(userId));
          setCurrentSelectedUserIds(userIds);
          // 修复：用 departmentUsersList 做映射，保证 customSelectedUsers 始终为完整对象
          const mappedUsers = userIds
            .map((userId: string) => (departmentUsersList || []).find((u: User) => String(u.id) === String(userId)))
            .filter(Boolean);
          setCustomSelectedUsers(mappedUsers);
          console.log('[ShareSettingModal] 回显 customSelectedUsers:', mappedUsers);
        }
      } else if (data.companyAuthorized === true) {
        setShareTo('org');
        setCurrentSelectedUserIds([]);
      } else {
        // ogs无数据（历史bot未初始化权限），默认视为公开访问
        setShareTo('visitByLink');
      }
    } catch (error) {
      console.error('[ShareSettingModal] Failed to fetch current permission:', error);
      setShareTo('org');
      setCurrentSelectedUserIds([]);
    } finally {
      setLoading(false);
    }
  };

  // 当弹窗打开时获取当前权限设置（等待 departmentUsersList 加载完成）
  useEffect(() => {
    if (opened && departmentUsersList) {
      fetchCurrentPermission();
    }
  }, [opened, botId, dataType, authorityCode, departmentUsersList]);

  // 更新下 Bot 的 ai_type
  const updateRobotInfo = () => {
    if (!robotInfo) return;

    return updateRobot({
      aiId: botId,
      name: robotInfo.name,
      subject_name: robotInfo.subject_name,
      discrible: robotInfo.discrible,
      robot_type: robotInfo.robot_type,
      prompt: robotInfo.prompt,
      ai_type: shareTo === 'visitByLink' ? 'public' : 'private',
    });
  };

  // 保存
  const handleSave = async () => {
    // 只有所有者才能保存分享设置
    if (!isOwner) return;

    setSaving(true);
    try {
      if (shareTo === 'me') {
        const currentUserId = getCurrentUserId();
        const [res] = await Promise.all([
          updateRobotInfo(),
          setResourcePrinciple({
            userIds: [currentUserId],
            departmentIds: [],
            companyAuthorized: false,
            dataId: botId,
            dataType,
            authorityCode,
          }),
        ]);
        if (res) {
          setRobotInfo(res.data);
        }
        showNotification({ message: t('shareSetting.saveSuccess'), color: 'green' });
        onCloseHandler();
      } else if (shareTo === 'org') {
        const [res] = await Promise.all([
          updateRobotInfo(),
          setResourcePrinciple({
            userIds: [],
            departmentIds: [],
            companyAuthorized: true,
            dataId: botId,
            dataType,
            authorityCode,
          }),
        ]);
        if (res) {
          setRobotInfo(res.data);
        }
        showNotification({ message: t('shareSetting.saveSuccess'), color: 'green' });
        onCloseHandler();
      } else if (shareTo === 'custom') {
        // 直接保存当前已选成员
        const [res] = await Promise.all([
          updateRobotInfo(),
          setResourcePrinciple({
            userIds: currentSelectedUserIds,
            departmentIds: [],
            companyAuthorized: false,
            dataId: botId,
            dataType,
            authorityCode,
          }),
        ]);
        if (res) {
          setRobotInfo(res.data);
        }
        showNotification({ message: t('shareSetting.saveSuccess'), color: 'green' });
        onCloseHandler();
      } else if (shareTo === 'visitByLink') {
        // 选项是互斥的，当选择这个选项时。同时将其他选项置空
        const [res] = await Promise.all([
          updateRobotInfo(),
          setResourcePrinciple({
            userIds: [],
            departmentIds: [],
            companyAuthorized: false,
            dataId: botId,
            dataType,
            authorityCode,
          }),
        ]);

        if (res) {
          setRobotInfo(res.data);
        }
        showNotification({ message: t('shareSetting.saveSuccess'), color: 'green' });
        onCloseHandler();
      }
    } catch (e: any) {
      showNotification({ message: e?.message || t('shareSetting.saveFailed'), color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  // 自定义成员选择确认
  const handleCustomConfirm = (users: User[]) => {
    // 只更新本地状态，不提交到服务器
    const userIds = users.map((u: User) => String(u.id));
    setCustomSelectedUsers(users);
    setCurrentSelectedUserIds(userIds);
    setLocalSelectorOpen(false);
    // 不调用API，不显示成功通知，不关闭弹窗
  };

  const getSaveButtonStatus = () => {
    if (!isOwner) return true;

    // 如果是自定义+没有选择任何用户，禁止保存
    if (shareTo === 'custom' && !currentSelectedUserIds.length) return true;

    return false;
  };

  return (
    <Modal
      opened={opened}
      onClose={onCloseHandler}
      title={
        <Text fw={600} fz={18}>
          {t('shareSetting.title')}
        </Text>
      }
      centered
      size={480}
    >
      <Box pb={8}>
        <Text fw={600} fz={16} mb={20}>
          {t('shareSetting.shareOptions')}
        </Text>
        <Box>
          {loading && ( // 加载状态
            <Box
              style={{
                padding: '40px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 16,
                border: '1.5px solid #E5E7EB',
                background: '#fff',
                marginBottom: 18,
              }}
            >
              <Text c="gray.5" size="sm">
                {t('shareSetting.loading')}
              </Text>
            </Box>
          )}

          {!loading &&
            shareTo !== undefined && ( // 选项卡片 - 只在非加载状态且有选中值时显示
              <>
                {[
                  // 选项卡片统一样式
                  {
                    value: 'me',
                    icon: <IconLock size={24} style={{ flexShrink: 0 }} />,
                    title: t('shareSetting.options.private.title'),
                    desc: t('shareSetting.options.private.desc'),
                    show: true, // 总是显示
                  },
                  // Cloud版有组织用户：显示组织内分享选项
                  {
                    value: 'org',
                    icon: <IconBuilding size={24} style={{ flexShrink: 0 }} />,
                    title: t('shareSetting.options.organization.title', '组织内共享'),
                    desc: t('shareSetting.options.organization.desc', '组织内所有成员都可访问'),
                    show: hasOrganization, // 仅Cloud版有组织用户显示
                  },
                ]
                  .filter((opt) => opt.show)
                  .map((opt) => (
                    <Box
                      key={opt.value}
                      onClick={() => {
                        // 只有所有者才能修改分享设置
                        if (!isOwner) return;
                        setShareTo(opt.value as any);
                      }}
                      className="relative group"
                      style={{
                        padding: '20px 24px',
                        borderRadius: 16,
                        border: shareTo === opt.value ? '2px solid #7C68FB' : '1.5px solid #E5E7EB',
                        background: '#fff',
                        marginBottom: 18,
                        boxShadow: shareTo === opt.value ? '0 2px 8px 0 rgba(124,104,251,0.08)' : 'none',
                        cursor: isOwner ? 'pointer' : 'not-allowed',
                        transition: 'border 0.2s, box-shadow 0.2s',
                        minHeight: 64,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 18,
                        opacity: isOwner ? 1 : 0.6,
                      }}
                    >
                      {opt.icon}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text fw={700} fz={15} mb={2} style={{ letterSpacing: 0.5 }}>
                          {opt.title}
                        </Text>
                        <Text fz={13} c="#8B95A1" style={{ lineHeight: 1.5 }}>
                          {opt.desc}
                        </Text>
                      </Box>
                      {shareTo === opt.value && (
                        <span style={{ position: 'absolute', right: 18, top: 18, color: '#7C68FB' }}>
                          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                            <circle cx="11" cy="11" r="11" fill="#F4F0FF" />
                            <path
                              d="M7 11.5L10 14.5L15 9.5"
                              stroke="#7C68FB"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      )}
                    </Box>
                  ))}

                <Box // 自定义选项卡片
                  onClick={() => {
                    // 只有所有者才能修改分享设置
                    if (!isOwner) return;
                    setShareTo('custom');
                  }}
                  className="relative group"
                  style={{
                    padding: '20px 24px',
                    borderRadius: 16,
                    border: shareTo === 'custom' ? '2px solid #7C68FB' : '1.5px solid #E5E7EB',
                    background: '#fff',
                    marginBottom: 8,
                    boxShadow: shareTo === 'custom' ? '0 2px 8px 0 rgba(124,104,251,0.08)' : 'none',
                    cursor: isOwner ? 'pointer' : 'not-allowed',
                    transition: 'border 0.2s, box-shadow 0.2s',
                    minHeight: 64,
                    opacity: isOwner ? 1 : 0.6,
                  }}
                >
                  <div className="flex items-center gap-4 mb-2">
                    <IconSettings size={24} style={{ flexShrink: 0 }} />
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={700} fz={15} mb={2} style={{ letterSpacing: 0.5 }}>
                        {t('shareSetting.options.custom.title')}
                      </Text>
                      <Text fz={13} c="#8B95A1" style={{ lineHeight: 1.5 }}>
                        {t('shareSetting.options.custom.desc')}
                      </Text>
                    </Box>
                    {shareTo === 'custom' && (
                      <span style={{ position: 'absolute', right: 18, top: 18, color: '#7C68FB' }}>
                        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                          <circle cx="11" cy="11" r="11" fill="#F4F0FF" />
                          <path
                            d="M7 11.5L10 14.5L15 9.5"
                            stroke="#7C68FB"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </div>
                  {shareTo === 'custom' && ( // 自定义内容展开区
                    <div style={{ marginTop: 12 }}>
                      <CustomUserMultiSelect
                        allUsers={departmentUsersList || []}
                        selectedUsers={customSelectedUsers}
                        setSelectedUsers={(users: any[]) => {
                          // 只有所有者才能修改用户选择
                          if (!isOwner) return;
                          setCustomSelectedUsers(users);
                          setCurrentSelectedUserIds(users.map((u: any) => String(u.id)));
                        }}
                        onOpenSelector={() => {
                          // 只有所有者才能打开选择器
                          if (!isOwner) return;
                          setLocalSelectorOpen(true);
                        }}
                        disabled={!isOwner}
                      />
                    </div>
                  )}
                </Box>
              </>
            )}
        </Box>
      </Box>
      {/* 开启公开访问区域 */}
      {!loading && (
        <Box
          onClick={() => {
            // 只有所有者才能修改分享设置
            if (!isOwner) return;
            setShareTo('visitByLink');
          }}
          className="relative group"
          style={{
            padding: '20px 24px',
            borderRadius: 16,
            border: shareTo === 'visitByLink' ? '2px solid #7C68FB' : '1.5px solid #E5E7EB',
            background: '#fff',
            marginBottom: 18,
            boxShadow: shareTo === 'visitByLink' ? '0 2px 8px 0 rgba(124,104,251,0.08)' : 'none',
            cursor: isOwner ? 'pointer' : 'not-allowed',
            transition: 'border 0.2s, box-shadow 0.2s',
            minHeight: 64,
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            opacity: isOwner ? 1 : 0.6,
          }}
        >
          <IconLock size={24} style={{ flexShrink: 0 }} />
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Text fw={700} fz={15} mb={2} style={{ letterSpacing: 0.5 }}>
              {t('shareSetting.options.public.title')}
            </Text>
            <Text fz={13} c="#8B95A1" style={{ lineHeight: 1.5 }}>
              {t('shareSetting.options.public.desc')}
            </Text>
            {shareTo === 'visitByLink' && (
              <span style={{ position: 'absolute', right: 18, top: 18, color: '#7C68FB' }}>
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="11" fill="#F4F0FF" />
                  <path
                    d="M7 11.5L10 14.5L15 9.5"
                    stroke="#7C68FB"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )}
          </Box>
        </Box>
      )}
      {/* 分享链接区域：OWNER在选择公开访问时显示，非OWNER始终显示（方便复制链接） */}
      {!loading && (shareTo === 'visitByLink' || !isOwner) && <Divider my={24} />}
      {!loading && (shareTo === 'visitByLink' || !isOwner) && (
        <Box>
          <Text fw={600} fz={16} mb={16}>
            {t('shareSetting.shareLink')}
          </Text>
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #E5E7EB',
              borderRadius: 8,
              padding: '12px 16px',
              backgroundColor: '#fff',
              gap: 12,
            }}
          >
            <IconLink size={20} color="#8B95A1" style={{ flexShrink: 0 }} />
            <Input
              value={shareLink}
              readOnly
              variant="unstyled"
              style={{
                flex: 1,
                fontSize: 14,
                color: '#374151',
                backgroundColor: 'transparent',
              }}
            />
            <CopyButton value={shareLink}>
              {({ copied, copy }) => (
                <Button
                  size="sm"
                  variant="light"
                  color="gray"
                  onClick={copy}
                  style={{
                    padding: '8px',
                    borderRadius: 6,
                    backgroundColor: copied ? '#10B981' : '#F3F4F6',
                    border: 'none',
                    color: copied ? '#fff' : '#374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    minWidth: 'auto',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                </Button>
              )}
            </CopyButton>
          </Box>
        </Box>
      )}
      {/* 二维码区域：与分享链接同步显示，方便扫码访问与下载（对齐个人模式分享逻辑） */}
      {!loading && (shareTo === 'visitByLink' || !isOwner) && (
        <Box mt={20}>
          <Stack align="center" gap={12}>
            <QRCode
              id={`share-setting-qrcode-${botId}`}
              value={shareLink}
              size={180}
              bgColor="#ffffff"
              fgColor="#000000"
              style={{ borderRadius: rem(8) }}
            />
            <Button
              size="sm"
              variant="light"
              color="gray"
              onClick={() =>
                downloadQRCode({
                  domId: `#share-setting-qrcode-${botId}`,
                  fileName: `share-bot-${botId}`,
                })
              }
              style={{ width: rem(180) }}
            >
              {t('settings.access_control.download_qrcode')}
            </Button>
          </Stack>
        </Box>
      )}
      {/* 带追加参数的链接与二维码（如楼层指南）：任意分享模式均可用，仅 bot 分享 */}
      {!loading && dataType === 'BOT' && (
        <Button
          fullWidth
          mt={20}
          variant="outline"
          leftSection={<IconLinkPlus size={16} />}
          onClick={() => setParamLinkModalOpened(true)}
        >
          {t('shareParamLink.entry_button')}
        </Button>
      )}
      {/* 分享页送信前需同意利用规约:任意分享模式均可配置,即时保存 */}
      {!loading && (
        <Group justify="flex-start" mt={20}>
          <Switch
            label={t('settings.access_control.consent_required')}
            description={t('consent.settings.content_edit_hint')}
            checked={requireConsent}
            disabled={!isOwner}
            onChange={(event) => handleConsentToggle(event.currentTarget.checked)}
          />
        </Group>
      )}
      {/* 嵌入到网站：仅在公开访问开启时显示 */}
      {!loading && shareTo === 'visitByLink' && (
        <>
          <Divider my={24} />
          <EmbedCodeBlock shareLink={shareLink} baseHost={embedBaseHost} />
        </>
      )}
      <Group justify="flex-end" mt={24}>
        <Button variant="outline" onClick={onCloseHandler}>
          {t('common.cancel')}
        </Button>
        <Button loading={saving} onClick={handleSave} disabled={getSaveButtonStatus()}>
          {t('common.saveChanges')}
        </Button>
      </Group>
      {/* 自定义成员选择弹窗 */}
      {localSelectorOpen && (
        <LocalMemberSelector
          open={localSelectorOpen}
          onClose={() => setLocalSelectorOpen(false)}
          onConfirm={(users: User[]) => {
            // 只有所有者才能确认用户选择
            if (!isOwner) return;
            // 用departmentUsersList做id映射，保证每个用户对象都带有name/email
            const mappedUsers = users.map((u: User) => {
              const fullUser = (departmentUsersList || []).find((d: User) => String(d.id) === String(u.id));
              return fullUser || u;
            });
            setCustomSelectedUsers(mappedUsers);
            setCurrentSelectedUserIds(mappedUsers.map((u: User) => String(u.id)));
            setLocalSelectorOpen(false);
          }}
          defaultSelectedUserIds={currentSelectedUserIds}
          isUserSelectable={(user: any) => {
            if (!isOwner) return false;
            // 分享场景下，查看者角色用户也可以被选择
            return true;
          }}
        />
      )}
      <ShareParamLinkModal
        opened={paramLinkModalOpened}
        onClose={() => setParamLinkModalOpened(false)}
        botId={botId}
        shareLink={shareLink}
        readOnly={!isOwner}
      />
    </Modal>
  );
};

export default ShareSettingModal;
